import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuiz } from '@/context/QuizContext';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FormPage = () => {
  const navigate = useNavigate();
  const { answers, calculateScores, setResultId } = useQuiz();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    whatsapp: '',
    company: '',
    revenue: '10-20k€'
  });
  const [phoneValue, setPhoneValue] = useState('');
  const [errors, setErrors] = useState({});
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    if (answers.length === 0) {
      navigate('/quiz');
    }
  }, [answers, navigate]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!phoneValue || phoneValue.length < 8) {
      newErrors.whatsapp = 'El WhatsApp es requerido';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'El nombre de la empresa es requerido';
    }

    if (!acceptedTerms) {
      newErrors.terms = 'Debes aceptar para continuar';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const scores = calculateScores();
      
      // Prepare data for backend with phone in international format
      const quizData = {
        answers: answers,
        lead_data: {
          ...formData,
          whatsapp: phoneValue || formData.whatsapp // Use phoneValue from PhoneInput
        },
        total_score: scores.total,
        block_scores: scores.blocks
      };

      const response = await axios.post(`${API}/quiz/submit`, quizData);
      
      if (response.data.result_id) {
        setResultId(response.data.result_id);
        
        // Track Facebook Pixel event
        if (window.fbq) {
          window.fbq('track', 'Lead', {
            content_name: 'Quiz Funnel',
            value: scores.total,
            currency: 'EUR'
          });
        }
        
        navigate(`/resultado/${response.data.result_id}`);
      }
    } catch (error) {
      alert('Hubo un error al enviar tus respuestas. Por favor inténtalo de nuevo.');
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Logo Header */}
      <div className="fixed top-4 left-4 md:top-8 md:left-8 z-50">
        <img 
          src="https://customer-assets.emergentagent.com/job_sales-audit-4/artifacts/laaotby8_v1_white.png" 
          alt="Sales Boost"
          className="h-10 md:h-12 lg:h-16 w-auto opacity-90"
        />
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16 md:py-24">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-12 mt-16 sm:mt-16"
        >
          <h1 
            className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 tracking-tighter"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="form-headline"
          >
            Tu diagnóstico está listo.
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-[#a3a3a3] px-2" data-testid="form-subheadline">
            Antes de mostrarte los resultados, necesito saber dónde enviarte el desglose completo con las recomendaciones personalizadas.
          </p>
        </motion.div>

        {/* Native Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-[#141414] border border-[#262626] rounded-md p-6 sm:p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="quiz-form">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium mb-2">
                Nombre completo *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#262626] rounded-md focus:border-[#802ef8] focus:ring-1 focus:ring-[#802ef8] outline-none transition-colors"
                placeholder="Juan Pérez"
                data-testid="form-name"
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#262626] rounded-md focus:border-[#802ef8] focus:ring-1 focus:ring-[#802ef8] outline-none transition-colors"
                placeholder="juan@miagencia.com"
                data-testid="form-email"
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>

            {/* WhatsApp with Country Selector */}
            <div>
              <label htmlFor="whatsapp" className="block text-sm font-medium mb-2">
                WhatsApp *
              </label>
              <PhoneInput
                international
                defaultCountry="ES"
                value={phoneValue}
                onChange={(value) => {
                  setPhoneValue(value);
                  setFormData(prev => ({ ...prev, whatsapp: value }));
                  if (errors.whatsapp) {
                    setErrors(prev => ({ ...prev, whatsapp: '' }));
                  }
                }}
                className="phone-input-custom"
                numberInputProps={{
                  className: "w-full px-4 py-3 bg-[#0a0a0a] border border-[#262626] rounded-md focus:border-[#802ef8] focus:ring-1 focus:ring-[#802ef8] outline-none transition-colors"
                }}
                data-testid="form-whatsapp"
              />
              {errors.whatsapp && <p className="text-red-500 text-sm mt-1">{errors.whatsapp}</p>}
            </div>

            {/* Company */}
            <div>
              <label htmlFor="company" className="block text-sm font-medium mb-2">
                Nombre de tu agencia/consultoría *
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#262626] rounded-md focus:border-[#802ef8] focus:ring-1 focus:ring-[#802ef8] outline-none transition-colors"
                placeholder="Mi Agencia Digital"
                data-testid="form-company"
              />
              {errors.company && <p className="text-red-500 text-sm mt-1">{errors.company}</p>}
            </div>

            {/* Revenue */}
            <div>
              <label htmlFor="revenue" className="block text-sm font-medium mb-2">
                Facturación mensual aproximada *
              </label>
              <select
                id="revenue"
                name="revenue"
                value={formData.revenue}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#262626] rounded-md focus:border-[#802ef8] focus:ring-1 focus:ring-[#802ef8] outline-none transition-colors"
                data-testid="form-revenue"
              >
                <option value="<5k€">&lt;5k€</option>
                <option value="5-10k€">5-10k€</option>
                <option value="10-20k€">10-20k€</option>
                <option value="20-30k€">20-30k€</option>
                <option value="30-50k€">30-50k€</option>
                <option value="+50k€">+50k€</option>
              </select>
            </div>

            {/* Consent Checkbox */}
            <div className="pt-2">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => {
                    setAcceptedTerms(e.target.checked);
                    if (e.target.checked && errors.terms) {
                      setErrors(prev => { const next = {...prev}; delete next.terms; return next; });
                    }
                  }}
                  className="mt-1 w-4 h-4 rounded border-[#262626] bg-[#0a0a0a] text-[#802ef8] focus:ring-[#802ef8] flex-shrink-0"
                  data-testid="form-consent-checkbox"
                />
                <span className="text-xs sm:text-sm text-[#a3a3a3] leading-relaxed group-hover:text-white transition-colors">
                  Acepto que se comuniquen conmigo por correo y WhatsApp para entregarme el diagnóstico, y acepto recibir ocasionalmente información comercial de parte de Sales Boost.
                </span>
              </label>
              {errors.terms && (
                <p className="text-red-500 text-xs mt-2 ml-7" data-testid="form-consent-error">{errors.terms}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-[#802ef8] hover:bg-[#9b5aff] rounded-md text-lg font-semibold transition-all hover:scale-105 shadow-lg shadow-[#802ef8]/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              data-testid="form-submit"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generando tu diagnóstico...
                </span>
              ) : (
                'Ver mis resultados →'
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default FormPage;